import React, { createContext, useState, useContext, useCallback } from 'react';

export interface OTPContextProps {
  email: string;
  signinOtpId: number;
  forgetPasswordOtpId: number;
  otp: number;
  setEmail: (email: string) => void;
  setSigninOtpId: (otpId: number) => void;
  setForgetPasswordOtpId: (otpId: number) => void;
  setOtp: (otp: number) => void;
  setOtpCreateTime: (createTime: number) => void;
  clearOTPInfo: () => void;
  handleSetEmail: (newEmail: string) => void;
  handleSetSigninOtpId: (newOtpId: number) => void;
  handleSetForgetPasswordOtpId: (newOtpId: number) => void;
  handleSetOtp: (newOtp: number) => void;
  handleSetOtpCreateTime: (createTime: number) => void;
  getEmail: () => string;
  getSigninOtpId: () => number;
  getForgetPasswordOtpId: () => number;
  getOTP: () => number;
  getOTPCreateTime: () => number;
}

export const OTPContext = createContext<OTPContextProps>({
  email: '',
  signinOtpId: 0,
  forgetPasswordOtpId: 0,
  otp: 0,
  setEmail: () => {},
  setSigninOtpId: () => {},
  setForgetPasswordOtpId: () => {},
  setOtp: () => {},
  setOtpCreateTime: () => {},
  clearOTPInfo: () => {},
  handleSetEmail: () => {},
  handleSetSigninOtpId: () => {},
  handleSetOtpCreateTime: () => {},
  handleSetForgetPasswordOtpId: () => {},
  handleSetOtp: () => {},
  getEmail: () => '',
  getSigninOtpId: () => 0,
  getForgetPasswordOtpId: () => 0,
  getOTP: () => 0,
  getOTPCreateTime: () => 0,
});

export const OTPContextProvider = ({ children }: React.PropsWithChildren) => {
  const [email, setEmail] = useState<string>('');
  const [signinOtpId, setSigninOtpId] = useState<number>(0);
  const [forgetPasswordOtpId, setForgetPasswordOtpId] = useState<number>(0);
  const [otp, setOtp] = useState<number>(0);
  const [otpCreateTime, setOtpCreateTime] = useState<number>(0);

  const handleSetEmail = useCallback((newEmail: string) => {
    setEmail(newEmail);
  }, []);

  const handleSetSigninOtpId = useCallback((newOtpId: number) => {
    setSigninOtpId(newOtpId);
  }, []);

  const handleSetForgetPasswordOtpId = useCallback((newOtpId: number) => {
    setForgetPasswordOtpId(newOtpId);
  }, []);

  const handleSetOtp = useCallback((newOtp: number) => {
    setOtp(newOtp);
  }, []);

  const handleSetOtpCreateTime = useCallback((createTime: number) => {
    setOtpCreateTime(createTime);
  }, []);

  const clearOTPInfo = () => {
    setEmail('');
    setSigninOtpId(0);
    setForgetPasswordOtpId(0);
    setOtp(0);
    handleSetOtpCreateTime(0);
  };

  const getEmail = () => email || '';
  const getSigninOtpId = () => signinOtpId || 0;
  const getForgetPasswordOtpId = () => forgetPasswordOtpId || 0;
  const getOTP = () => otp || 0;
  const getOTPCreateTime = () => {
    return otpCreateTime || 0;
  };

  return (
    <OTPContext.Provider
      value={{
        email,
        signinOtpId,
        forgetPasswordOtpId,
        otp,
        setEmail: handleSetEmail,
        setSigninOtpId: handleSetSigninOtpId,
        setForgetPasswordOtpId: handleSetForgetPasswordOtpId,
        setOtp: handleSetOtp,
        setOtpCreateTime: handleSetOtpCreateTime,
        clearOTPInfo,
        handleSetEmail,
        handleSetSigninOtpId,
        handleSetForgetPasswordOtpId,
        handleSetOtp,
        handleSetOtpCreateTime,
        getEmail,
        getSigninOtpId,
        getForgetPasswordOtpId,
        getOTP,
        getOTPCreateTime,
      }}
    >
      {children}
    </OTPContext.Provider>
  );
};

export default OTPContext;

export const useOTPContext = (): OTPContextProps => {
  return useContext(OTPContext);
};
